// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import uuidv1 from 'uuid/v1';
import { useState, ReactNode } from 'react';
import { ThemeConsumer } from 'styled-components';
import { CSSTransition } from 'react-transition-group';
import { defineMessages, injectIntl } from 'react-intl';
import ReactSelect, { components, Creatable } from 'react-select';

import Icon from '@legacy/moon/particles/Icon';

import getTheme from './generateTheme';
import {
  Wrapper,
  Description,
  DropdownIcon,
  ImgStyled,
  Label,
  dropdownStyles,
  RemoveIcon,
  Checkmark,
  MenuContainer,
  PlaceholderIcon,
} from './styles';

const DropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <DropdownIcon
      moon
      icon="select_arrow"
      theme={props.theme}
      isDisabled={props.isDisabled}
      menuIsOpen={props.selectProps.menuIsOpen}
      customDropdownIcon={props.customDropdownIcon}
    />
  </components.DropdownIndicator>
);

const MultiValueRemove = (props: any) => (
  <components.MultiValueRemove {...props}>
    <RemoveIcon
      moon
      icon="x_cross"
      theme={props.theme}
      isDisabled={props.isDisabled}
      isFocused={props.isFocused}
      aria-label={props.data.value && props.data.value ? `remove-${props.data.value}` : 'remove'}
      role="button"
    />
  </components.MultiValueRemove>
);

const Menu = ({ selectProps, children, ...props }: any) => {
  const { menuColumns } = selectProps;

  return (
    <components.Menu {...props} selectProps={selectProps}>
      <MenuContainer columns={menuColumns}>{children}</MenuContainer>
    </components.Menu>
  );
};

const Option = ({ children, ...props }: any) => {
  const { isMulti, isSelected } = props;

  return (
    <components.Option {...props}>
      {isMulti && (
        <Checkmark selected={isSelected}>
          <Icon moon icon="val_check" />
        </Checkmark>
      )}
      {children}
    </components.Option>
  );
};

const ValueContainer = ({ children, ...props }: any) => {
  const { hasValue, getValue, isMulti } = props;
  const data = hasValue ? getValue()[0] : {};
  const hasImage = data.selectedImageUrl || data.imageUrl;

  return (
    <components.ValueContainer {...props}>
      {hasImage && !isMulti && <ImgStyled src={hasImage} />}
      {children}
    </components.ValueContainer>
  );
};

const Control = ({ children, ...props }: any) => {
  const {
    isDisabled,
    hasValue,
    isFocused,
    theme,
    selectProps: { inputId, placeholder, error: hasError, icon },
  } = props;

  return (
    <components.Control {...props}>
      {placeholder && (
        <>
          {icon && (
            // @ts-ignore
            <PlaceholderIcon moon icon={icon} isDisabled={isDisabled} error={hasError} isOpen={isFocused || hasValue} />
          )}
          <Label
            htmlFor={inputId}
            // @ts-ignore
            key={`${inputId}-label`}
            // @ts-ignore
            isOpen={isFocused || hasValue}
            isDisabled={isDisabled}
            hasError={hasError}
            hasIcon={Boolean(icon)}
            theme={theme}
          >
            {placeholder}
          </Label>
        </>
      )}
      {children}
    </components.Control>
  );
};

const GroupHeading = (props: any) => {
  const { className, cx, getStyles, theme, children, ...cleanProps } = props;

  const {
    colors: { background, group },
  } = theme;

  const lineStyles = {
    backgroundColor: group.default,
    height: '1px',
    position: 'absolute',
    top: '6px',
    left: '4px',
    right: '4px',
  };
  const textStyles = {
    padding: '0 12px',
    backgroundColor: background,
    position: 'relative',
  };

  return (
    <div
      css={getStyles('groupHeading', { theme, ...cleanProps })}
      className={cx({ 'group-heading': true }, className)}
      {...cleanProps}
    >
      <div css={lineStyles} />
      <span style={textStyles}>{children}</span>
    </div>
  );
};

const messages = defineMessages({
  noOptionsMessage: {
    id: 'Select.NoOptionsMessage',
    defaultMessage: 'No se encontraron opciones',
  },
});

interface SelectProps {
  className?: string;
  id?: string;
  intl: IntlShape;
  alternate?: boolean;
  placeholder?: string;
  minInputLength?: number;
  options?: any[];
  defaultValue?: any;
  onChange?: (value: any) => void;
  onMenuClose?: () => void;
  onMenuOpen?: () => void;
  value?: any;
  instructionLabel?: string;
  isDisabled?: boolean;
  error?: ReactNode;
  margin?: string;
  icon?: string;
  allowCreatable?: boolean;
  isSearchable?: boolean;
  hideDropdownIndicator?: boolean;
  isCompact?: boolean;
}

const Select = (props: SelectProps) => {
  const [state, setState] = useState({
    inputIsOpen: false,
    value: {},
    showMenu: false,
  });
  const {
    alternate,
    onChange,
    onMenuOpen,
    onMenuClose,
    intl: { formatMessage },
    minInputLength,
    placeholder,
    id,
    className,
    instructionLabel: description,
    margin,
    allowCreatable,
    isSearchable = true,
    hideDropdownIndicator,
    isCompact = false,
    ...rest
  } = props;

  const { error } = rest;

  const menuOpen = () => {
    const { value } = state;
    if (!('value' in value)) setState(st => ({ ...st, inputIsOpen: true, showMenu: false }));
    if (onMenuOpen) onMenuOpen();
  };

  const menuClose = () => {
    const { value } = state;
    if (!('value' in value)) setState(st => ({ ...st, inputIsOpen: false, showMenu: false }));
    if (onMenuClose) onMenuClose();
  };

  const handleChange = (value: any) => {
    setState(st => ({ ...st, value }));
    if (onChange) onChange(value);
  };

  const handleInputChange = (value: string, action: any) => {
    if (action.action === 'input-change') {
      setState(st => ({ ...st, showMenu: value.length >= minInputLength }));
    }
  };

  const getProps = product => ({
    placeholder,
    onMenuOpen: menuOpen,
    onMenuClose: menuClose,
    onChange: handleChange,
    inputId: id,
    onInputChange: handleInputChange,
    isSearchable: !isCompact && isSearchable,
    showMenu: state.showMenu || minInputLength === 0,
    noOptionsMessage: () => formatMessage(messages.noOptionsMessage),
    components: {
      Menu,
      Control,
      ValueContainer,
      GroupHeading,
      DropdownIndicator: hideDropdownIndicator ? null : DropdownIndicator,
      MultiValueRemove,
      Option,
      IndicatorSeparator: () => null,
      Placeholder: () => null,
    },
    styles: dropdownStyles,
    theme: theme => ({
      ...theme,
      ...getTheme(theme, product, isCompact, alternate),
    }),
    hideSelectedOptions: false,
    ...rest,
  });

  const { isDisabled: disabled } = rest;

  return (
    <Wrapper className={className} margin={margin}>
      <ThemeConsumer>
        {({ product }) =>
          allowCreatable ? <Creatable {...getProps(product)} /> : <ReactSelect {...getProps(product)} />
        }
      </ThemeConsumer>
      <CSSTransition in={Boolean(error)} timeout={200} classNames="input-description-transition" unmountOnExit>
        <Description error={error} disabled={disabled}>
          {error}
        </Description>
      </CSSTransition>
      <CSSTransition
        in={Boolean(description && !error)}
        timeout={200}
        classNames="input-description-transition"
        unmountOnExit
      >
        <Description error={error} disabled={disabled}>
          {description}
        </Description>
      </CSSTransition>
    </Wrapper>
  );
};

Select.defaultProps = {
  className: '',
  id: uuidv1(),
  placeholder: '',
  minInputLength: 0,
  onChange: undefined,
  onMenuClose: undefined,
  onMenuOpen: undefined,
  value: undefined,
  instructionLabel: undefined,
  isDisabled: false,
  error: undefined,
  margin: '0 0 4px 0',
  icon: '',
  allowCreatable: false,
  hideDropdownIndicator: false,
  isCompact: false,
  alternate: false,
  isSearchable: true,
};

export default injectIntl(Select);
